import React, { useState } from 'react';

import { Chip, IconButton, Menu, MenuItem } from '@mui/material';
import { AlineTooltip } from 'src/components';
import { DownloadIcon } from 'src/components/icons/DownloadIcon';
import {
  useDownloadDOCXWithTrackedChangesCheck,
  useDownloadJSONWithLoading,
  useIsJsonDownloadAvailable,
} from 'src/editor/toolbars/menus';
import { useDownloadFileVersionWithLoading, useGetCurrentDoc } from 'src/hooks';
import { useEditor } from 'src/layout-engine';
import { themeV5 } from 'src/theme';

interface DownloadDocButtonProps {
  documentId: UUID;
  disabled?: boolean;
}

// TODO: If changing, consider migrating to DeclarativeMenu
export function DownloadDocButton({ disabled, documentId }: DownloadDocButtonProps): JSX.Element {
  const editor = useEditor();
  const { document } = useGetCurrentDoc();
  const [open, setOpen] = useState<boolean>(false);
  const [anchorEl, setAnchorEl] = useState<HTMLDivElement | null>(null);
  const { downloadFileVersionWithLoading } = useDownloadFileVersionWithLoading();
  const { downloadDOCXWithTrackedChangesCheck } = useDownloadDOCXWithTrackedChangesCheck();
  const { downloadJSONWithLoading } = useDownloadJSONWithLoading();
  const isNonSignedAlineDoc =
    document?.latestVersion.__typename === 'DocumentVersion' && !document.isSigned;
  const isJsonDownloadAvailable = useIsJsonDownloadAvailable();

  if (isNonSignedAlineDoc) {
    return (
      <>
        <AlineTooltip arrow placement="bottom" title="Download">
          <div ref={setAnchorEl}>
            <IconButton
              aria-label="Download"
              disabled={disabled}
              size="small"
              onClick={() => setOpen(!open)}
            >
              <DownloadIcon />
            </IconButton>
          </div>
        </AlineTooltip>
        <Menu
          anchorEl={anchorEl}
          anchorOrigin={{
            horizontal: 'right',
            vertical: 'top',
          }}
          open={open}
          transformOrigin={{ horizontal: 'right', vertical: 'top' }}
          onClose={() => setOpen(false)}
        >
          <MenuItem
            onClick={async () => {
              setOpen(false);
              await downloadDOCXWithTrackedChangesCheck(documentId, {
                docParts: editor.children,
                name: document?.name || 'Untitled document',
              });
            }}
          >
            Microsoft Word (.docx)
          </MenuItem>
          <MenuItem
            onClick={async () => {
              setOpen(false);
              if (document?.latestVersion)
                await downloadFileVersionWithLoading(documentId, {
                  exportName: document.name,
                  fileVersion: 'latestVersion',
                });
            }}
          >
            PDF (.pdf)
          </MenuItem>
          {isJsonDownloadAvailable && (
            <MenuItem
              style={{ gap: themeV5.spacing(0.5) }}
              onClick={async () => {
                setOpen(false);
                await downloadJSONWithLoading(documentId, {
                  docParts: editor.children,
                  name: document?.name || 'Untitled document',
                });
              }}
            >
              JSON (.json)
              <Chip label="Dev only" size="small" />
            </MenuItem>
          )}
        </Menu>
      </>
    );
  }

  return (
    <AlineTooltip arrow placement="bottom" title="Download">
      <div ref={setAnchorEl}>
        <IconButton
          aria-label="Download"
          disabled={disabled}
          size="small"
          onClick={async () => {
            if (document?.latestVersion)
              await downloadFileVersionWithLoading(documentId, {
                exportName: document.name,
                fileVersion: 'latestVersion',
              });
          }}
        >
          <DownloadIcon />
        </IconButton>
      </div>
    </AlineTooltip>
  );
}
